/**
 * The **Arena draft + results overlays** (Arena Slice E, PRD #334 / ADR-0177).
 *
 * The **draft overlay** is the one interactive piece of the run UI: an
 * {@link ArenaDraftMessage} pauses the run server-side (ADR-0176 freezes the run
 * world *and* rejects walks), so this modal can sit as long as the runner thinks -
 * no client timer. It renders the seeded options as numbered buttons - a title
 * line via {@link describeArenaOption} over a plain-language effect line via
 * {@link describeArenaOptionEffect}; clicking one (or the 1/2/3
 * keyboard nicety) reports the index through `onPick` - `main.ts` sends the
 * `PickArenaOption` command - and hides. An invalid pick server-side leaves the
 * offer standing (the server re-sends nothing), and a pick whose banked XP rolls
 * the *next* draft immediately re-shows with the fresh offer.
 *
 * The **results overlay** is purely visual: by the time {@link ArenaResultsMessage}
 * arrives the server has already ended the run (and returns the runner to town on
 * the settle), so the dismiss button only hides the panel - nothing rides the wire.
 *
 * Both follow the client-chrome discipline: plain-DOM sibling overlays of the
 * canvas (markup/CSS in `index.html`), `createElement`/`textContent` only, missing
 * markup tolerated, and a `reset()` reconnect guard (the PR #243 lesson) so a
 * mid-anything reconnect never strands a stale panel over the town view.
 */
import { formatCountdown } from './hud';
import { formatArenaScore, formatGold } from './leaderboard';
import {
  ARENA_ASCENSION_COOLDOWN_CUT_PER_PICK,
  ARENA_ASCENSION_DAMAGE_PER_PICK,
  ARENA_FIREBALL_NAME,
  ARENA_KEYSTONES,
  skillProjectileCount,
  skillProjectileDamage,
  skillCooldownTicks,
  skillRange,
  explosionRadius,
  explosionDamage,
  chainHops,
  flameStrikeChance,
  novaInterval,
  novaRange,
  scorchedTrailFraction,
  scorchedTrailWidth,
  arenaMoveMsPerTile,
  arenaCastSettleTicks,
  type ArenaKeystoneId,
  type ArenaModifierId,
  type ArenaStatId,
} from './sim/arenaWeapons';
import type { ArenaDraftOption } from './sim/arena';
import type { ArenaDraftMessage, ArenaResultsMessage } from './net/protocol';
import { ARENA_TICK_MS } from './arenaHud';
import { getArenaClass } from './sim/arenaClasses';
import { ARENA_CAST_SETTLE_TICKS, ARENA_PLAYER_MS_PER_TILE } from './sim/arena';
import { arenaMagnetRadius } from './sim/xpGem';

/** Friendly labels for the five draftable fire modifiers (the draft/HUD copy). */
export const MODIFIER_LABELS: Record<ArenaModifierId, string> = {
  'more-projectiles': 'More Projectiles',
  'greater-projectile': 'Greater Projectile',
  explosion: 'Explosion',
  chain: 'Chain',
  'flame-strike': 'Flame Strike',
  nova: 'Nova',
  'scorched-earth': 'Scorched Earth',
  // Archer (PRD #446 Slice 1, ADR-0237).
  'double-shot': 'Double Shot',
  'heavy-draw': 'Heavy Draw',
  ricochet: 'Ricochet',
  'barbed-arrows': 'Barbed Arrows',
  'poison-arrows': 'Poison Arrows',
  'eagle-eye': 'Eagle Eye',
  caltrops: 'Caltrops',
};

/** Friendly labels + a one-line transformation pitch per keystone (PRD #378):
 * a keystone is a build-defining moment, so its row says what changes. */
export const KEYSTONE_LABELS: Record<ArenaKeystoneId, { name: string; effect: string }> = {
  comet: { name: 'Comet', effect: 'your fireball hurls the struck enemy backward' },
  fork: { name: 'Fork', effect: 'every chain hop splits in two' },
  cataclysm: { name: 'Cataclysm', effect: 'bigger blasts that leave burning ground' },
  meteor: { name: 'Meteor', effect: 'the proc becomes a falling star on the thickest pack' },
  'serpent-spiral': { name: 'Serpent Spiral', effect: 'your nova rings piercing fire serpents' },
  combustion: { name: 'Combustion', effect: 'enemies that die burning explode, chaining through the pack' },
  splinter: { name: 'Splinter', effect: 'every projectile bursts into a ring of sub-arrows on hit' },
  // Archer keystones (PRD #446 Slice 1, ADR-0237) - labels declared now; the
  // keystones themselves trail (unofferable until their follow-up PRs land).
  'arrow-storm': { name: 'Arrow Storm', effect: 'a telegraphed rain of arrows falls on the thickest pack' },
  'endless-ricochet': { name: 'Endless Ricochet', effect: 'your arrows bounce until they run out of targets' },
  headshot: { name: 'Headshot', effect: 'critical hits instantly kill enemies below a fifth of their health' },
  hemorrhage: { name: 'Hemorrhage', effect: 'bleeding enemies burst on death, spreading the wound' },
};

/** Friendly labels for the six draftable passive stats (the draft/HUD copy). */
const STAT_LABELS: Record<ArenaStatId, string> = {
  damage: 'Damage',
  cooldown: 'Cooldown',
  area: 'Area',
  regen: 'Regeneration',
  'xp-gain': 'XP Gain',
  mobility: 'Mobility',
  magnet: 'Magnet',
};

/**
 * The plain-language "what does this actually do" line shown under each modifier
 * option (PRD #334 follow-up: the draft named the upgrades but never explained
 * them). Deliberately qualitative - the exact numbers live in the sim and are
 * tuned per slice, so a fixed sentence stays true where a hard number would rot.
 */
const MODIFIER_EFFECTS: Record<ArenaModifierId, string> = {
  'more-projectiles': 'Adds another projectile to every automatic volley, letting each cast hit more of the horde.',
  'greater-projectile': 'Transforms your projectile into a larger, harder-hitting fireball.',
  explosion: 'Every projectile impact bursts and damages all enemies caught inside its blast radius.',
  chain: 'A hit arcs to another nearby enemy, extending each projectile through the pack.',
  'flame-strike': 'Secondary hits can call down a pillar of flame for concentrated area damage.',
  nova: 'Periodically erupts a complete ring of fire around you, clearing enemies at close range.',
  'scorched-earth': 'Movement leaves burning ground behind you that damages pursuing enemies.',
  // Archer (PRD #446 Slice 1, ADR-0237).
  'double-shot': 'Every volley looses another arrow at the next-nearest enemy.',
  'heavy-draw': 'Replaces the bow with a crossbow that fires more slowly but deals substantially more damage.',
  ricochet: 'Arrows bounce from their first target to another nearby enemy.',
  'barbed-arrows': 'Arrow hits leave a bleeding wound that continues dealing damage over time.',
  'poison-arrows': 'Slain enemies burst into a lingering poison cloud that damages enemies inside it.',
  'eagle-eye': 'Every arrow gains a chance to critically hit for increased damage.',
  caltrops: 'Movement scatters caltrops behind you that damage and slow pursuing enemies.',
};

/** The same one-line effect copy for each drafted passive stat. */
const STAT_EFFECTS: Record<ArenaStatId, string> = {
  damage: 'Increases the damage of every projectile, triggered effect, and area attack you deal.',
  cooldown: 'Shortens the delay between automatic volleys so your main attack fires more often.',
  area: 'Expands the radius of explosions, novas, flame pillars, and other area effects.',
  regen: 'Continuously restores health during the run, including while you move and attack.',
  'xp-gain': 'Increases the experience awarded when collected XP gems reach you.',
  mobility: 'Increases movement speed and helps the Fire Mage resume casting sooner after moving.',
  magnet: 'Increases the distance from which dropped XP gems are pulled toward you.',
};

/**
 * A modifier pick's effect parenthetical, derived from the same pure math the
 * sim fires with (never re-invented copy): the projectile count a
 * more-projectiles pick lands on, and the fireball transformation the first
 * greater-projectile pick is. Other modifiers read fine as bare "-> Lv N".
 */
function modifierEffect(modifier: ArenaModifierId, toLevel: number): string {
  if (modifier === 'more-projectiles') {
    return ` (${skillProjectileCount({ 'more-projectiles': toLevel })} projectiles)`;
  }
  if (modifier === 'greater-projectile' && toLevel === 1) return ` (${ARENA_FIREBALL_NAME}!)`;
  return '';
}

/**
 * One draft option's **title** line (pure, unit-tested): a fire-modifier raise
 * with the level it lands on (`toLevel` rides the wire so nothing is
 * re-derived) or a passive-stat raise. The "what it does" prose is split into
 * {@link describeArenaOptionEffect} so the overlay can render it as a second,
 * quieter line. An unknown id degrades to the raw id - never a throw (the wire
 * is server-authored, but stay defensive).
 */
export function describeArenaOption(option: ArenaDraftOption): string {
  switch (option.kind) {
    case 'modifier':
      return `${MODIFIER_LABELS[option.modifier] ?? option.modifier} → Lv ${option.toLevel}${modifierEffect(option.modifier, option.toLevel)}`;
    case 'stat':
      return `${STAT_LABELS[option.stat] ?? option.stat} → Lv ${option.toLevel}`;
    case 'keystone': {
      const keystone = ARENA_KEYSTONES[option.modifier];
      const label = keystone ? KEYSTONE_LABELS[keystone] : undefined;
      if (!label) return `★ Keystone: ${option.modifier}`; // defensive: unknown wire value
      return `★ ${label.name}`;
    }
    case 'vortex':
      return 'Flame Vortex';
    case 'ascension':
      return option.ascension === 'power'
        ? `Ascend: Power ×${option.count}`
        : `Ascend: Alacrity ×${option.count}`;
  }
}

/**
 * One draft option's **effect** line: the plain-language "what does this do"
 * sentence shown beneath the title. Modifiers/stats read from the fixed effect
 * copy above; keystones reuse their transformation pitch (sentence-cased into a
 * standalone line); vortex/ascension carry their own. Returns `''` for anything
 * without copy (an unknown wire id), and the overlay then renders no second line.
 */
export function describeArenaOptionEffect(option: ArenaDraftOption): string {
  switch (option.kind) {
    case 'modifier':
      return MODIFIER_EFFECTS[option.modifier] ?? '';
    case 'stat':
      return STAT_EFFECTS[option.stat] ?? '';
    case 'keystone': {
      const keystone = ARENA_KEYSTONES[option.modifier];
      const label = keystone ? KEYSTONE_LABELS[keystone] : undefined;
      if (!label) return '';
      return `${label.effect.charAt(0).toUpperCase()}${label.effect.slice(1)}.`;
    }
    case 'vortex':
      return 'Summons a fiery ally that hunts the horde.';
    case 'ascension':
      return option.ascension === 'power'
        ? `+${Math.round(ARENA_ASCENSION_DAMAGE_PER_PICK * 100)}% damage per stack.`
        : `−${Math.round(ARENA_ASCENSION_COOLDOWN_CUT_PER_PICK * 100)}% cooldown per stack.`;
  }
}

/** Exact before/after detail derived from the same pure math as combat. */
export function describeArenaOptionDelta(
  option: ArenaDraftOption,
  msg: Pick<ArenaDraftMessage, 'build' | 'classId'>,
): string {
  if (!msg.build || !msg.classId) return '';
  const classDef = getArenaClass(msg.classId);
  if (!classDef) return '';
  const mods = { ...msg.build.modifiers };
  const stats = { ...msg.build.stats };
  const beforeMods = { ...mods };
  const beforeStats = { ...stats };
  if (option.kind === 'modifier') mods[option.modifier] = option.toLevel;
  if (option.kind === 'stat') stats[option.stat] = option.toLevel;
  const pair = (a: string | number, b: string | number, unit = '') => `${a}${unit} → ${b}${unit}`;
  if (option.kind === 'modifier') switch (option.modifier) {
    case 'more-projectiles': return `${pair(skillProjectileCount(beforeMods), skillProjectileCount(mods))} projectiles`;
    case 'greater-projectile': return `${pair(skillProjectileDamage(beforeMods, beforeStats.damage ?? 0), skillProjectileDamage(mods, stats.damage ?? 0))} damage`;
    case 'explosion': return `${pair(explosionRadius(beforeMods, beforeStats.area ?? 0), explosionRadius(mods, stats.area ?? 0))} tiles; ${pair(explosionDamage(100, beforeMods), explosionDamage(100, mods), '%')} damage`;
    case 'chain': return `${pair(chainHops(beforeMods), chainHops(mods))} hops`;
    case 'flame-strike': return pair(Math.round(flameStrikeChance(beforeMods) * 100), Math.round(flameStrikeChance(mods) * 100), '% proc');
    case 'nova': return `Every ${pair(novaInterval(beforeMods) || '—', novaInterval(mods))} volleys; ${pair(novaRange(beforeMods, beforeStats.area ?? 0), novaRange(mods, stats.area ?? 0))} tiles`;
    case 'scorched-earth': return `${pair(Math.round(scorchedTrailFraction(beforeMods) * 100), Math.round(scorchedTrailFraction(mods) * 100), '% damage')}; ${pair(scorchedTrailWidth(beforeMods), scorchedTrailWidth(mods))} tiles wide`;
    default: return '';
  }
  if (option.kind === 'stat') switch (option.stat) {
    case 'damage': return `${pair(skillProjectileDamage(mods, beforeStats.damage ?? 0), skillProjectileDamage(mods, stats.damage ?? 0))} primary damage`;
    case 'cooldown': return pair((skillCooldownTicks(beforeStats.cooldown ?? 0, 0, classDef.skill.cooldownTicks) * ARENA_TICK_MS / 1000).toFixed(2), (skillCooldownTicks(stats.cooldown ?? 0, 0, classDef.skill.cooldownTicks) * ARENA_TICK_MS / 1000).toFixed(2), 's cooldown');
    case 'area': return pair(skillRange(beforeStats.area ?? 0, classDef.skill.range).toFixed(1), skillRange(stats.area ?? 0, classDef.skill.range).toFixed(1), ' tiles');
    case 'regen': return pair(beforeStats.regen ?? 0, stats.regen ?? 0, ' HP / 2s');
    case 'xp-gain': return pair((beforeStats['xp-gain'] ?? 0) * 15, (stats['xp-gain'] ?? 0) * 15, '% XP');
    case 'mobility': {
      const gait = pair(arenaMoveMsPerTile(ARENA_PLAYER_MS_PER_TILE, beforeStats.mobility ?? 0), arenaMoveMsPerTile(ARENA_PLAYER_MS_PER_TILE, stats.mobility ?? 0), ' ms/tile');
      if (classDef.skill.firesWhileMoving) return gait;
      return `${gait}; ${pair(arenaCastSettleTicks(ARENA_CAST_SETTLE_TICKS, beforeStats.mobility ?? 0, false) * ARENA_TICK_MS / 1000, arenaCastSettleTicks(ARENA_CAST_SETTLE_TICKS, stats.mobility ?? 0, false) * ARENA_TICK_MS / 1000, 's settle')}`;
    }
    case 'magnet': return pair(arenaMagnetRadius(beforeStats.magnet ?? 0), arenaMagnetRadius(stats.magnet ?? 0), ' tiles');
  }
  return '';
}

/** Options for {@link initArenaDraftOverlay}; `doc` is injected by tests. */
export interface ArenaDraftOverlayOptions {
  /** Called with the picked option index; `main.ts` sends `PickArenaOption`. */
  onPick: (index: number) => void;
  /** The document to wire (defaults to the live `document`). */
  doc?: Document;
}

/** The handle `main.ts` drives the draft overlay through. */
export interface ArenaDraftOverlayHandle {
  /** Show (or re-show, replacing the options) the pending draft offer. */
  show(msg: ArenaDraftMessage): void;
  /** Hide without picking (e.g. the run ended out from under the pause). */
  hide(): void;
  /** Whether the overlay is currently up (the 1/2/3 keys act only while open). */
  isOpen(): boolean;
  /** Hide + clear - the reconnect guard (a forfeited run's draft never resumes). */
  reset(): void;
}

/**
 * Wire the `#arena-draft` overlay (declared hidden in `index.html`). The option
 * buttons are rebuilt per offer (an offer is at most a handful of rows); the
 * keyboard nicety listens on `window` but acts only while the overlay is open and
 * no text field is focused, so typing "1" in chat never picks.
 */
export function initArenaDraftOverlay(opts: ArenaDraftOverlayOptions): ArenaDraftOverlayHandle {
  const doc = opts.doc ?? document;
  const root = doc.getElementById('arena-draft');
  const title = doc.getElementById('arena-draft-title');
  const optionsBox = doc.getElementById('arena-draft-options');

  let open = false;
  let optionCount = 0;

  // A draft can appear while the player is holding right-click to steer on the
  // canvas. The browser fires `contextmenu` after the press, by which point this
  // newly-mounted modal is under the pointer instead of the canvas (whose own
  // context-menu guard therefore never sees it). Keep that in-flight movement
  // gesture from opening the native menu over the choices.
  root?.addEventListener('contextmenu', (e) => e.preventDefault());

  const hide = (): void => {
    open = false;
    if (root) root.hidden = true;
  };

  const pick = (index: number): void => {
    // Hide first: the pick either resumes the run (a status push repaints the HUD)
    // or rolls the next draft (a fresh ArenaDraft re-shows this overlay at once).
    //
    // DEFENSIVE NOTE (softlock hazard if server semantics ever change): a
    // *rejected* pick is silent by contract - ADR-0176 leaves the pause and the
    // offer standing and re-sends nothing. Today that cannot strand the runner,
    // because every index this overlay can send is valid against the offer it
    // rendered (the buttons/keys are built from the same options list), so a
    // rejection only happens to a hand-crafted client. But if pick rejection ever
    // becomes reachable from here (e.g. options that can go stale server-side
    // without a re-send), hiding before the send would leave the run paused with
    // no visible modal and no message to re-show it - a softlock. In that world,
    // keep the overlay up until a resume status / fresh draft arrives instead.
    hide();
    opts.onPick(index);
  };

  const isTextEntryFocused = (): boolean => {
    const el = doc.activeElement;
    if (!(el instanceof HTMLElement)) return false;
    return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
  };

  // The 1/2/3 keyboard nicety: index by digit while the overlay is open. Installed
  // once for the page (the overlay handle lives for the session, like the boards).
  (doc.defaultView ?? window).addEventListener('keydown', (e: KeyboardEvent) => {
    if (!open || isTextEntryFocused()) return;
    const digit = Number.parseInt(e.key, 10);
    if (!Number.isInteger(digit) || digit < 1 || digit > optionCount) return;
    e.preventDefault();
    pick(digit - 1);
  });

  return {
    show(msg) {
      if (!root || !optionsBox) return;
      if (title) title.textContent = `Level ${msg.level}! Choose an upgrade`;
      // Rebuild the rows per offer: an offer replaces the previous one wholesale
      // (a pick that banks into the next level re-sends a fresh offer).
      optionsBox.replaceChildren();
      for (const [i, option] of msg.options.entries()) {
        const button = doc.createElement('button');
        button.type = 'button';
        button.className = 'arena-draft-option';
        // Two stacked lines: the numbered title, then the quieter effect prose.
        // textContent, never innerHTML - the copy is server-derived / fixed, but the
        // chrome discipline holds everywhere.
        const optionTitle = doc.createElement('span');
        optionTitle.className = 'arena-draft-option-title';
        optionTitle.textContent = `${i + 1}. ${describeArenaOption(option)}`;
        button.appendChild(optionTitle);
        const delta = describeArenaOptionDelta(option, msg);
        if (delta) {
          const exact = doc.createElement('span');
          exact.className = 'arena-draft-option-values';
          exact.textContent = delta;
          button.appendChild(exact);
        }
        const effect = describeArenaOptionEffect(option);
        if (effect) {
          const detail = doc.createElement('span');
          detail.className = 'arena-draft-option-detail';
          detail.textContent = effect;
          button.appendChild(detail);
        }
        button.addEventListener('click', () => pick(i));
        optionsBox.appendChild(button);
      }
      optionCount = msg.options.length;
      open = true;
      root.hidden = false;
    },
    hide,
    isOpen: () => open,
    reset: hide,
  };
}

/** Options for {@link initArenaResultsOverlay}; `doc` is injected by tests. */
export interface ArenaResultsOverlayOptions {
  /** The document to wire (defaults to the live `document`). */
  doc?: Document;
}

/** The handle `main.ts` drives the results overlay through. */
export interface ArenaResultsOverlayHandle {
  /**
   * Show the run's final tally (time survived, kills, score, gold), plus the
   * client-tracked **best combo** (PRD #432 Slice C) appended locally from the
   * {@link ../comboTracker.ComboTracker}'s `bestThisRun` - the server's
   * {@link ArenaResultsMessage} carries no combo field (cosmetic, off-wire, so
   * a spoofed client can only lie to itself). Omit / pass 0 to hide the line.
   */
  show(msg: ArenaResultsMessage, bestCombo?: number): void;
  /** Hide - the reconnect guard (nothing to dismiss on a fresh connection). */
  reset(): void;
}

/**
 * Wire the `#arena-results` overlay (declared hidden in `index.html`). Dismissal
 * is purely visual - the server already returned the runner to town on the settle.
 */
export function initArenaResultsOverlay(
  opts: ArenaResultsOverlayOptions = {},
): ArenaResultsOverlayHandle {
  const doc = opts.doc ?? document;
  const root = doc.getElementById('arena-results');
  const title = doc.getElementById('arena-results-title');
  const time = doc.getElementById('arena-results-time');
  const kills = doc.getElementById('arena-results-kills');
  const score = doc.getElementById('arena-results-score');
  const gold = doc.getElementById('arena-results-gold');
  const combo = doc.getElementById('arena-results-combo');
  const dismiss = doc.getElementById('arena-results-dismiss');

  const hide = (): void => {
    if (root) root.hidden = true;
  };
  dismiss?.addEventListener('click', hide);

  return {
    show(msg, bestCombo = 0) {
      if (!root) return;
      if (title) {
        // The arena is endless (ADR-0210) - every run ends when the horde takes
        // you; the achievement is the score, headlined below.
        title.textContent = msg.challengeName
          ? `${msg.challengeName}: the horde got you.`
          : 'The horde got you.';
      }
      // `formatCountdown` renders elapsed ticks (50ms each) as the same M:SS the
      // run clock used, so "time survived" reads in the clock's own units.
      renderResultRow(doc, time, 'Time survived', formatCountdown(msg.ticks * ARENA_TICK_MS));
      renderResultRow(doc, kills, 'Kills', String(msg.kills));
      renderResultRow(doc, score, 'Score', formatArenaScore(msg.score));
      renderResultRow(doc, gold, 'Gold earned', formatGold(msg.gold));
      // The run's peak combo (PRD #432 Slice C): a purely local line appended
      // from the client's combo machine - the intensity stat to the score's
      // long-game number, so a run's hottest moment survives its death. Hidden
      // when the run never chained (nothing to brag).
      if (combo) {
        if (bestCombo > 0) {
          renderResultRow(doc, combo, 'Best combo', `${bestCombo}×`);
          combo.hidden = false;
        } else {
          combo.hidden = true;
        }
      }
      root.hidden = false;
    },
    reset: hide,
  };
}

/** Render one semantic label/value row in the Arena result scorecard. */
function renderResultRow(
  doc: Document,
  row: HTMLElement | null,
  label: string,
  value: string,
): void {
  if (!row) return;
  const labelEl = doc.createElement('span');
  labelEl.className = 'arena-result-label';
  labelEl.textContent = label;
  const valueEl = doc.createElement('span');
  valueEl.className = 'arena-result-value';
  valueEl.textContent = value;
  row.replaceChildren(labelEl, valueEl);
  row.setAttribute('aria-label', `${label}: ${value}`);
}
